import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Tab, Tabs, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, FormControl, InputLabel, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton } from '@mui/material';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { UpdateDealTicketRequest, UpdatePositionModel, LocationModel, PositionModel, Side, AllocationModel, PositionType } from '../../../../Generated/Commodity-Management-Api';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { StoneXNumber, StoneXTextField, StoneXAutocomplete } from '../../../StoneXMui';
import { HotTable } from '@handsontable/react-wrapper';
import Handsontable from 'handsontable';
import { CellChange, CellValue, ChangeSource, RangeType } from 'handsontable/common';
import "handsontable/dist/handsontable.full.min.css";
import { DropdownEditor } from 'handsontable/editors';
import { ColumnSettings } from 'handsontable/settings';
import 'handsontable/styles/ht-theme-main.min.css';
import { months, monthToNumber } from '../../../../Helpers';
import { createRoot } from 'react-dom/client';

interface OffsetPositionsModalProps {
    open: boolean;
    onClose: () => void;
    positions: PositionModel[];
    onSuccess: () => void;
    locations: LocationModel[];
    allocations: AllocationModel[];
}

const OffsetPositionsModal: React.FC<OffsetPositionsModalProps> = ({ open, onClose, positions, onSuccess, locations, allocations }) => {
    const [quantities, setQuantities] = useState<Record<number, number | string>>({});
    const [selectedLocations, setSelectedLocations] = useState<Record<number, number | string>>({});
    const [prices, setPrices] = useState<Record<number, number | string>>({});
    const { enqueueSnackbar } = useSnackbar();
    const [isSaving, setIsSaving] = useState(false);
    const [selectedAllocations, setSelectedAllocations] = useState<Record<number, number>>({});

    const hotRef = useRef<any>(null);

    useEffect(() => {
        if (open) {
            const initialQuantities: Record<number, number | string> = {};
            const initialLocations: Record<number, number | string> = {};
            const initialPrices: Record<number, number | string> = {};
            positions.forEach(pos => {
                initialQuantities[pos.positionId!] = '';
                initialLocations[pos.positionId!] = '';
                initialPrices[pos.positionId!] = '';
            });
            setQuantities(initialQuantities);
            setSelectedLocations(initialLocations);
            setPrices(initialPrices);
        }
    }, [open, positions]);

    const handleQuantityChange = (positionId: number, maxQuantity: number, value: string) => {
        const numericValue = value === '' ? '' : Number(value);
        if (numericValue === '' || (numericValue >= 0 && numericValue <= maxQuantity)) {
            setQuantities(prev => ({ ...prev, [positionId]: numericValue }));
        }
    };

    const handleLocationChange = (positionId: number, locationId: string) => {
        setSelectedLocations(prev => ({ ...prev, [positionId]: Number(locationId) }));
    };

    const handlePriceChange = (positionId: number, value: string) => {
        const numericValue = value === '' ? '' : Number(value);
        if (numericValue === '' || !isNaN(Number(numericValue))) {
            setPrices(prev => ({ ...prev, [positionId]: numericValue }));
        }
    };

    const handleAllocationChange = (positionId: number, allocationGroupId: number) => {
        setSelectedAllocations(prev => ({ ...prev, [positionId]: allocationGroupId }));
    };


    class CustomDropdown extends DropdownEditor {
        limitDropdownIfNeeded(spaceAvailable: any, dropdownHeight: any): void {
            super.limitDropdownIfNeeded(spaceAvailable, dropdownHeight);
            const newHeight = (this.cellProperties?.source?.length ?? 0) * 40;
            this.htEditor.updateSettings({ height: newHeight + 4, width: '100%' });
        }

        // Override flipDropdown to prevent negative top
        flipDropdown(dropdownHeight: number) {
            // Do not set negative top, always open below
            const dropdownStyle = this.htEditor.rootElement.style;
            dropdownStyle.position = 'absolute';
            dropdownStyle.top = '0px';
        }
    }

    const priceRenderer = function (_instance: any, TD: any, _row: any, _col: any, _prop: any, value: any, cellProperties: any) {
        if (!TD.__root) {
            TD.__root = createRoot(TD);
        }
        const element = <StoneXNumber number={value} decimals={3} allowMoreDecimals ifNoNumber='' className={cellProperties.readOnly ? 'htDimmed' : undefined} />;
        TD.__root.render(element);
    };
    function numericValidator(value: CellValue, callback: (valid: boolean) => void) {
        const number = parseFloat(value as string);
        const isValidVolume = !isNaN(number) && number > 0;

        if (value === null || value === undefined || value === '' || !isValidVolume) {
            callback(false); // Invalid
        } else {
            callback(true); // Valid
        }
    }

    Handsontable.renderers.registerRenderer('priceRenderer', priceRenderer);
    // Register the Custom Editor
    Handsontable.editors.registerEditor("customDropdown", CustomDropdown);
    Handsontable.validators.registerValidator('numericValidator', numericValidator);

    const columns: ColumnSettings[] = [


        {
            data: 'positionId',

        }, {
            data: 'tradeDate',
            className: 'numeric',
            renderer: (instance, TD, row, col, prop, value, cellProperties) => {
                TD.textContent = value ? dayjs(value).format('MM/DD/YYYY') : '';
            }
        },
        {
            data: 'contractDate.label',
        },
        {
            data: 'side',
            type: 'dropdown',
            editor: 'customDropdown',
            source: ["Buy", "Sell"],
            validator: (value, callback) => callback(!!value && ["Buy", "Sell"].includes(value))
        },
        {
            data: 'price',
            className: 'numeric',
            renderer: priceRenderer,
        },
        {
            data: 'volume',
            className: 'numeric',
        },
        {
            data: 'offsetPrice',
            className: 'numeric',
            renderer: priceRenderer,
        },
        {
            data: 'offsetQuantity',
            className: 'numeric',
            validator: 'numericValidator',
        },
        {
            data: 'location',
            type: 'dropdown',
            editor: 'customDropdown',
            source: (locations ?? []).map(alloc => alloc.locationName ?? '')
        }
    ];

    const handleProceed = async () => {
        setIsSaving(true);
        const promises = [];

        for (const position of positions) {
            const positionId = position.positionId!;
            const offsetQuantity = quantities[positionId];
            const locationId = selectedLocations[positionId];
            const price = prices[positionId];
            const allocationGroupId = selectedAllocations[positionId];

            if (offsetQuantity && Number(offsetQuantity) > 0 && locationId && (price !== '' && price !== undefined)) {
                const promise = async () => {
                    const offsettingPosition: UpdatePositionModel = {
                        ...position,
                        positionId: undefined,
                        side: position.side === Side.Buy ? Side.Sell : Side.Buy,
                        volume: Number(offsetQuantity),
                        locationId: Number(locationId),
                        price: Number(price),
                    };

                    const dealTicket: UpdateDealTicketRequest = {
                        tradeDate: dayjs().format(),
                        commodityId: position.commodity.commodityId,
                        dealTicketNumber: `Offset-${position.dealTicketNumber}-${Date.now()}`,
                        counterpartyId: position.counterparty.counterpartyId,
                        positions: [offsettingPosition],
                    };

                    await CommodityManagementApi.addTrade(null, dealTicket);
                };
                promises.push(promise());
            }
        }

        if (promises.length === 0) {
            enqueueSnackbar('No positions to offset. Please enter a quantity, price, and select a location.', { variant: 'warning' });
            setIsSaving(false);
            return;
        }

        try {
            await Promise.all(promises);
            enqueueSnackbar(`${promises.length} position(s) offset successfully.`, { variant: 'success' });
            onSuccess();
        } catch (error) {
            console.error("Failed to offset positions", error);
            enqueueSnackbar('An error occurred while offsetting positions.', { variant: 'error' });
        } finally {
            setIsSaving(false);
            onClose();
        }
    };
    const cells = (row: any, col: any, prop: any) => {
        const cellProperties = {} as any;
        const hotInstance = hotRef.current.hotInstance;
        const positionType = hotInstance.getDataAtRow(row)[1];

        const readonlyCells = ['optionType', 'optionStyle', 'optionStrike'];

        if (isSaving) {
            cellProperties.readOnly = true;
            return cellProperties;
        }

        if (readonlyCells.includes(prop)) {
            if (positionType === PositionType.Option) {
                cellProperties.readOnly = false;
                cellProperties.className = '';
            }
            else {
                cellProperties.readOnly = true;
                cellProperties.className = 'htDisabled';
            }
        }

        return cellProperties;
    };
    function handleAfterChange(changes: CellChange[] | null, source: ChangeSource) {
        if (changes) {
            const hot = hotRef.current.hotInstance;

            const positionIdColIndex = hot.propToCol('positionId');
            const volumeColIndex = hot.propToCol('volume');

            changes.forEach(([rowIndex, prop, oldValue, newValue]) => {
                const rowData = hot.getDataAtRow(rowIndex);
                const positionId = rowData[positionIdColIndex];

                if (prop === 'offsetQuantity') {
                    const volume = rowData[volumeColIndex];
                    handleQuantityChange(positionId, volume, newValue as string);
                } else if (prop === 'offsetPrice') {
                    handlePriceChange(positionId, newValue as string);
                } else if (prop === 'location') {
                    const location = locations.find(l => l.locationName === newValue);
                    if (location) {
                        handleLocationChange(positionId, location.locationId.toString());
                    }
                }
            });
        }

    };
    function beforePaste(data: CellValue[][], coords: RangeType[]) {
        const monthMap: { [key: string]: number } = {
            "january": 1, "jan": 1,
            "february": 2, "feb": 2,
            "march": 3, "mar": 3,
            "april": 4, "apr": 4,
            "may": 5,
            "june": 6, "jun": 6,
            "july": 7, "jul": 7,
            "august": 8, "aug": 8,
            "september": 9, "sep": 9,
            "october": 10, "oct": 10,
            "november": 11, "nov": 11,
            "december": 12, "dec": 12
        };
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Offset Positions</DialogTitle>
            <DialogContent>
                <HotTable
                    ref={hotRef}
                    data={useMemo(() => positions.map(position => ({
                        ...position,
                        budgetDate: {
                            ...position.budgetDate,
                            month: position.budgetDate?.month ? months[position.budgetDate.month - 1] : null
                        },
                        contractDate: {
                            ...position.contractDate,
                            month: position.contractDate?.month ? months[position.contractDate.month - 1] : null
                        },
                        location: '', 
                    })), [positions])}
                    stretchH="all"
                    width="100%"
                    height="auto"
                    colHeaders={[
                        "Id",
                        "Trade Date",
                        "Contract Date",
                        "Side", 
                        "Original Price",
                        "Original Quantity",
                        "Offset Price",
                        "Offset Quantity",
                        "Location"
                    ]}
                    hiddenColumns={{
                        columns: [0],
                        indicators: false,
                        copyPasteEnabled: false
                    }}
                    columns={columns}
                    autoWrapRow={true}
                    autoWrapCol={true}
                    rowHeights={40}
                    columnHeaderHeight={48}
                    minSpareRows={0}
                    cells={cells}
                    afterChange={handleAfterChange}
                    beforePaste={beforePaste}
                    licenseKey="29ae0-f35ad-79c41-2472d-2c70d"
                />
                
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button onClick={handleProceed} disabled={isSaving}>{isSaving ? 'Processing...' : 'Proceed'}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default OffsetPositionsModal;